<script setup lang="ts">
import type { Guest, RsvpResponse } from "@/models"
import type { WelcomeInvitationConfig } from "@/models/welcomeInvitationConfig"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { InvitationRsvpForm } from "@/components"
import { BaseModal, Button } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { GuestDetailsModalKey } from "@/symbols"

interface GuestDetailsModalProps {
  guest?: Guest
  eventVaultId?: number
  invitation?: WelcomeInvitationConfig
}

const props = defineProps<GuestDetailsModalProps>()

const emit = defineEmits<{
  (e: "success"): void
  (e: "error"): void
  (e: "cancel"): void
}>()

defineExpose({
  clearFormValues,
})

const queryClient = useQueryClient()
const { t } = useTranslation()

const { toggle: toggleModal } = useModal(GuestDetailsModalKey)

// Key the form on guest id so it remounts (and resets) whenever the selected guest changes
const formKey = ref(0)

const guestAsRsvpResponse = computed<RsvpResponse | undefined>(() => {
  if (!props.guest)
    return undefined
  return {
    eventGuestId: props.guest.id,
    name: props.guest.name,
    email: props.guest.email,
    additionalGuests: props.guest.additionalGuests,
    rsvpDetails: {
      rsvpStatus: props.guest.rsvpStatus,
      guestRSVPComment: props.guest.rsvpComment,
      dietaryRequirements: props.guest.dietaryRequirements,
      songRequests: props.guest.songRequests,
      guestCount: props.guest.rsvpCount,
    },
  }
})

function clearFormValues() {
  formKey.value++
}

function handleCancel() {
  emit("cancel")
  clearFormValues()
  toggleModal()
}

async function handleSuccess() {
  emit("success")
  await queryClient.invalidateQueries({ queryKey: ["listGuestsV1", props.eventVaultId] })
  clearFormValues()
  toggleModal()
}
</script>

<template>
  <BaseModal :name="GuestDetailsModalKey" data-test="guest-details-modal">
    <div class="bg-white rounded-lg overflow-hidden max-h-[80vh] flex flex-col">
      <div class="flex-shrink-0 bg-white p-6 pb-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="font-heading font-semibold text-xl">
              {{ guest?.id ? t("guest_details_modal.title_edit_guest") : t("guest_details_modal.title_add_new_guest") }}
            </h1>
            <p v-if="!guest?.id" class="text-sm font-body text-label">
              {{ t("guest_details_modal.description_add_new_guest") }}
            </p>
            <p v-else class="text-sm font-body text-label">
              {{ t("guest_details_modal.description_edit_guest") }}
            </p>
          </div>
          <Button variant="flat" prefix-icon="Close" class="p-0" @click="handleCancel">
            <span class="sr-only">{{ t("guest_details_modal.close_sr") }}</span>
          </Button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <InvitationRsvpForm
          :key="formKey"
          mode="host"
          :event-vault-id="eventVaultId"
          :guest-id="guest?.id"
          :guest-response="guestAsRsvpResponse"
          :invitation="invitation"
          @close="handleCancel"
          @success="handleSuccess"
          @error="emit('error')"
        />
      </div>
    </div>
  </BaseModal>
</template>
